import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';
import { supabase } from '../../../utils/supabaseClient';
import { 
  SaveIcon, 
  SearchIcon, 
  FilterIcon,
  BlockIcon,
  CheckIcon,
  UserIcon,
  TrashIcon,
  EditIcon
} from '../../../utils/IconWrapper';

const Container = styled.div`
  padding: ${designSystem.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.xl};
  gap: ${designSystem.spacing.lg};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  ${commonStyles.titleLarge}
  margin: 0;
  flex: 1;
`;

const StatsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.xl};
`;

const StatCard = styled(motion.div)`
  ${commonStyles.card}
  text-align: center;
  position: relative;
  overflow: hidden;

  .icon {
    font-size: ${designSystem.typography.fontSize['3xl']};
    margin-bottom: ${designSystem.spacing.md};
  }

  .number {
    font-size: ${designSystem.typography.fontSize['2xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .label {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${designSystem.colors.background.gradient};
  }
`;

const ControlsSection = styled.div`
  display: flex;
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.xl};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  input {
    ${commonStyles.input}
    padding-left: 48px;
  }

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: ${designSystem.colors.text.secondary};
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  ${commonStyles.input}
  min-width: 150px;
  cursor: pointer;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          color: #ffffff;
          border: 2px solid ${designSystem.colors.status.error};
        `;
      case 'success':
        return `
          background: ${designSystem.colors.status.success};
          color: #ffffff;
          border: 2px solid ${designSystem.colors.status.success};
        `;
      case 'secondary':
        return commonStyles.buttonSecondary;
      default:
        return commonStyles.buttonPrimary;
    }
  }}
  
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  font-size: ${designSystem.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${designSystem.spacing.md};
  justify-content: flex-end;
  align-items: center;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  margin-bottom: ${designSystem.spacing.xl};
  border-bottom: 2px solid ${designSystem.colors.border.light};
  overflow-x: auto;
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? designSystem.colors.primary.main : 'transparent'};
  color: ${props => props.active ? '#000000' : designSystem.colors.text.secondary};
  border: none;
  padding: ${designSystem.spacing.md} ${designSystem.spacing.lg};
  border-radius: ${designSystem.borderRadius.lg} ${designSystem.borderRadius.lg} 0 0;
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  white-space: nowrap;
  position: relative;
  
  &:hover {
    background: ${props => props.active ? designSystem.colors.primary.main : designSystem.colors.primary.main + '20'};
    color: ${props => props.active ? '#000000' : designSystem.colors.primary.main};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.active ? designSystem.colors.primary.main : 'transparent'};
  }
`;

const ListContainer = styled.div`
  ${commonStyles.card}
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: ${designSystem.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${designSystem.colors.border.light};
  }

  th {
    background: ${designSystem.colors.background.secondary};
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: ${designSystem.typography.fontSize.xs};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr {
    transition: all ${designSystem.transitions.normal};
    
    &:hover {
      background: ${designSystem.colors.background.secondary};
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.md};
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${designSystem.colors.background.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: #000000;
    font-size: ${designSystem.typography.fontSize.sm};
  }

  .user-details {
    .name {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
      margin-bottom: 2px;
    }
    
    .email {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }
`;

const StatusBadge = styled.span<{ status: 'blacklisted' | 'whitelisted' | 'active' | 'suspended' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  ${props => {
    switch (props.status) {
      case 'whitelisted':
        return `
          background: ${designSystem.colors.status.success}20;
          color: ${designSystem.colors.status.success};
        `;
      case 'blacklisted':
        return `
          background: ${designSystem.colors.status.error}20;
          color: ${designSystem.colors.status.error};
        `;
      case 'suspended':
        return `
          background: ${designSystem.colors.status.warning}20;
          color: ${designSystem.colors.status.warning};
        `;
      default:
        return `
          background: ${designSystem.colors.secondary.main}20;
          color: ${designSystem.colors.secondary.main};
        `;
    }
  }}
`;

const SeverityBadge = styled.span<{ severity: 'low' | 'medium' | 'high' | 'critical' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.md};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.bold};
  text-transform: uppercase;
  
  ${props => {
    switch (props.severity) {
      case 'critical':
        return `
          background: #FF0000;
          color: #ffffff;
        `;
      case 'high':
        return `
          background: ${designSystem.colors.status.error};
          color: #ffffff;
        `;
      case 'medium':
        return `
          background: ${designSystem.colors.status.warning};
          color: #000000;
        `;
      case 'low':
        return `
          background: ${designSystem.colors.status.info};
          color: #ffffff;
        `;
      default:
        return `
          background: ${designSystem.colors.secondary.main};
          color: #ffffff;
        `;
    }
  }}
`;

const ReasonCell = styled.div`
  max-width: 200px;
  
  .reason {
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.medium};
    margin-bottom: 4px;
  }
  
  .note {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${designSystem.spacing.xs};
  align-items: center;
`;

const QuickAction = styled.button<{ variant?: 'edit' | 'delete' | 'approve' | 'block' }>`
  background: transparent;
  border: none;
  padding: ${designSystem.spacing.xs};
  border-radius: ${designSystem.borderRadius.md};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.variant) {
      case 'approve':
        return `
          color: ${designSystem.colors.status.success};
          &:hover { background: ${designSystem.colors.status.success}20; }
        `;
      case 'block':
        return `
          color: ${designSystem.colors.status.error};
          &:hover { background: ${designSystem.colors.status.error}20; }
        `;
      case 'edit':
        return `
          color: ${designSystem.colors.primary.main};
          &:hover { background: ${designSystem.colors.primary.main}20; }
        `;
      case 'delete':
        return `
          color: ${designSystem.colors.status.error};
          &:hover { background: ${designSystem.colors.status.error}20; }
        `;
      default:
        return `
          color: ${designSystem.colors.text.secondary};
          &:hover { background: ${designSystem.colors.background.secondary}; }
        `;
    }
  }}
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${designSystem.spacing.lg};
`;

const ModalContent = styled(motion.div)`
  ${commonStyles.card}
  width: 600px;
  max-width: 90vw;
  position: relative;
`;

const FormGroup = styled.div`
  margin-bottom: ${designSystem.spacing.lg};

  label {
    display: block;
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.medium};
    margin-bottom: ${designSystem.spacing.sm};
  }

  input, select, textarea {
    ${commonStyles.input}
    width: 100%;
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .helper-text {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    margin-top: ${designSystem.spacing.xs};
  }
`;

interface BlacklistItem {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  type: 'blacklist' | 'whitelist' | 'suspended';
  reason: string;
  notes: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  created_by: string;
  expires_at?: string;
  is_permanent: boolean;
}

const BlacklistPanel: React.FC = () => {
  const [items, setItems] = useState<BlacklistItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<BlacklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<BlacklistItem | null>(null);
  const [stats, setStats] = useState({
    blacklisted: 0,
    whitelisted: 0,
    suspended: 0,
    total: 0
  });
  
  const [formData, setFormData] = useState({
    user_email: '',
    type: 'blacklist' as 'blacklist' | 'whitelist' | 'suspended',
    reason: '',
    notes: '',
    severity: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    is_permanent: false,
    expires_at: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterItems();
  }, [items, activeTab, searchTerm, severityFilter]);

  const loadData = async () => {
    try {
      // Simula dati blacklist/whitelist
      const mockData: BlacklistItem[] = [
        {
          id: '1',
          user_id: 'user_1',
          user_name: 'Mario Rossi',
          user_email: 'mario.rossi@email.com',
          type: 'blacklist',
          reason: 'Danni alla proprietà',
          notes: 'Ha causato danni significativi all\'appartamento durante l\'ultimo soggiorno. Comportamento aggressivo verso il personale.',
          severity: 'high',
          created_at: '2024-01-15',
          created_by: 'admin@j-o-h.com',
          is_permanent: true
        },
        {
          id: '2',
          user_id: 'user_2',
          user_name: 'Elena Bianchi',
          user_email: 'elena.bianchi@email.com',
          type: 'whitelist',
          reason: 'Cliente VIP',
          notes: 'Cliente eccellente con oltre 50 prenotazioni. Sempre rispettoso e pulito. Merita trattamento prioritario.',
          severity: 'low',
          created_at: '2024-01-10',
          created_by: 'admin@j-o-h.com',
          is_permanent: true
        },
        {
          id: '3',
          user_id: 'user_3',
          user_name: 'Giovanni Verdi',
          user_email: 'giovanni.verdi@email.com',
          type: 'suspended',
          reason: 'Pagamenti in ritardo',
          notes: 'Multipli pagamenti in ritardo negli ultimi 3 mesi. Sospeso temporaneamente fino a regolarizzazione.',
          severity: 'medium',
          created_at: '2024-01-12',
          created_by: 'admin@j-o-h.com',
          expires_at: '2024-02-12',
          is_permanent: false
        },
        {
          id: '4',
          user_id: 'user_4',
          user_name: 'Anna Neri',
          user_email: 'anna.neri@email.com',
          type: 'blacklist',
          reason: 'Attività sospetta',
          notes: 'Tentativo di frode con carta di credito. Identità non verificata. Potenziale rischio sicurezza.',
          severity: 'critical',
          created_at: '2024-01-18',
          created_by: 'admin@j-o-h.com',
          is_permanent: true
        }
      ];

      setItems(mockData);
      
      // Calcola statistiche
      const stats = {
        blacklisted: mockData.filter(item => item.type === 'blacklist').length,
        whitelisted: mockData.filter(item => item.type === 'whitelist').length,
        suspended: mockData.filter(item => item.type === 'suspended').length,
        total: mockData.length
      };
      setStats(stats);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    // Filtro per tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(item => item.type === activeTab);
    }

    // Filtro per ricerca
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.reason.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro per severità
    if (severityFilter !== 'all') {
      filtered = filtered.filter(item => item.severity === severityFilter);
    }

    setFilteredItems(filtered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // In produzione salvrebbe nel database
      const newItem: BlacklistItem = {
        id: Date.now().toString(),
        user_id: 'new_user',
        user_name: 'Nome da caricare',
        user_email: formData.user_email,
        type: formData.type,
        reason: formData.reason,
        notes: formData.notes,
        severity: formData.severity,
        created_at: new Date().toISOString(),
        created_by: 'admin@j-o-h.com',
        expires_at: formData.expires_at || undefined,
        is_permanent: formData.is_permanent
      };

      if (editingItem) {
        // Modifica item esistente
        setItems(prev => prev.map(item => 
          item.id === editingItem.id ? { ...item, ...newItem, id: item.id } : item
        ));
      } else {
        // Aggiungi nuovo item
        setItems(prev => [newItem, ...prev]);
      }

      // Reset form
      setFormData({
        user_email: '',
        type: 'blacklist',
        reason: '',
        notes: '',
        severity: 'medium',
        is_permanent: false,
        expires_at: ''
      });
      setEditingItem(null);
      setShowModal(false);

      alert(editingItem ? 'Item aggiornato con successo!' : 'Item aggiunto con successo!');
    } catch (error) {
      console.error('Errore salvataggio:', error);
      alert('Errore durante il salvataggio');
    }
  };

  const handleEdit = (item: BlacklistItem) => {
    setEditingItem(item);
    setFormData({
      user_email: item.user_email,
      type: item.type,
      reason: item.reason,
      notes: item.notes,
      severity: item.severity,
      is_permanent: item.is_permanent,
      expires_at: item.expires_at || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo item?')) {
      setItems(prev => prev.filter(item => item.id !== id));
      alert('Item eliminato con successo!');
    }
  };

  const getInitials = (name: string, email: string) => {
    if (name && name !== 'Nome da caricare') {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'blacklist': return '🚫';
      case 'whitelist': return '✅';
      case 'suspended': return '⏸️';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['3xl'], marginBottom: designSystem.spacing.md }}>
            ⏳
          </div>
          Caricamento gestione blacklist...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>🛡️ Gestione Blacklist/Whitelist</Title>
        <ActionButton onClick={() => {
          setEditingItem(null);
          setFormData({
            user_email: '',
            type: 'blacklist',
            reason: '',
            notes: '',
            severity: 'medium',
            is_permanent: false,
            expires_at: ''
          });
          setShowModal(true);
        }}>
          ➕ Aggiungi Item
        </ActionButton>
      </Header>

      <StatsCards>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="icon">🚫</div>
          <div className="number">{stats.blacklisted}</div>
          <div className="label">Blacklisted</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="icon">✅</div>
          <div className="number">{stats.whitelisted}</div>
          <div className="label">Whitelisted</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="icon">⏸️</div>
          <div className="number">{stats.suspended}</div>
          <div className="label">Sospesi</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="icon">📋</div>
          <div className="number">{stats.total}</div>
          <div className="label">Totale</div>
        </StatCard>
      </StatsCards>

      <ControlsSection>
        <SearchContainer>
          <SearchIcon className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Cerca per nome, email o motivo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterContainer>
          <FilterSelect
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <option value="all">Tutte le Severità</option>
            <option value="critical">Critica</option>
            <option value="high">Alta</option>
            <option value="medium">Media</option>
            <option value="low">Bassa</option>
          </FilterSelect>
        </FilterContainer>
      </ControlsSection>

      <TabsContainer>
        <TabButton active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          📋 Tutti ({items.length})
        </TabButton>
        <TabButton active={activeTab === 'blacklist'} onClick={() => setActiveTab('blacklist')}>
          🚫 Blacklist ({stats.blacklisted})
        </TabButton>
        <TabButton active={activeTab === 'whitelist'} onClick={() => setActiveTab('whitelist')}>
          ✅ Whitelist ({stats.whitelisted})
        </TabButton>
        <TabButton active={activeTab === 'suspended'} onClick={() => setActiveTab('suspended')}>
          ⏸️ Sospesi ({stats.suspended})
        </TabButton>
      </TabsContainer>

      <ListContainer>
        <Table>
          <thead>
            <tr>
              <th>Utente</th>
              <th>Tipo</th>
              <th>Motivo</th>
              <th>Severità</th>
              <th>Data</th>
              <th>Scadenza</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => (
              <tr key={item.id}>
                <td>
                  <div className="user-info">
                    <div className="avatar">
                      {getInitials(item.user_name, item.user_email)}
                    </div>
                    <div className="user-details">
                      <div className="name">{item.user_name}</div>
                      <div className="email">{item.user_email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <StatusBadge status={item.type === 'blacklist' ? 'blacklisted' : item.type === 'whitelist' ? 'whitelisted' : item.type}>
                    {getTypeIcon(item.type)} {item.type}
                  </StatusBadge>
                </td>
                <td>
                  <ReasonCell>
                    <div className="reason">{item.reason}</div>
                    <div className="note" title={item.notes}>{item.notes}</div>
                  </ReasonCell>
                </td>
                <td>
                  <SeverityBadge severity={item.severity}>
                    {getSeverityIcon(item.severity)} {item.severity}
                  </SeverityBadge>
                </td>
                <td>{new Date(item.created_at).toLocaleDateString('it-IT')}</td>
                <td>
                  {item.is_permanent ? (
                    <StatusBadge status="blacklisted">♾️ Permanente</StatusBadge>
                  ) : item.expires_at ? (
                    new Date(item.expires_at).toLocaleDateString('it-IT')
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <ActionsCell>
                    <QuickAction
                      variant="edit"
                      onClick={() => handleEdit(item)}
                      title="Modifica"
                    >
                      <EditIcon size={16} />
                    </QuickAction>
                    
                    <QuickAction
                      variant="delete"
                      onClick={() => handleDelete(item.id)}
                      title="Elimina"
                    >
                      <TrashIcon size={16} />
                    </QuickAction>
                  </ActionsCell>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {filteredItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: designSystem.spacing.xl, color: designSystem.colors.text.secondary }}>
            {searchTerm || severityFilter !== 'all' || activeTab !== 'all'
              ? 'Nessun item trovato con i filtri applicati'
              : 'Nessun item in blacklist/whitelist'
            }
          </div>
        )}
      </ListContainer>

      <AnimatePresence>
        {showModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <Header>
                <h2>{editingItem ? '✏️ Modifica Item' : '➕ Aggiungi Nuovo Item'}</h2>
                <button onClick={() => setShowModal(false)}>✖️</button>
              </Header>

              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <label>Email Utente</label>
                  <input
                    type="email"
                    value={formData.user_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                    required
                    placeholder="utente@email.com"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Tipo</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                    required
                  >
                    <option value="blacklist">🚫 Blacklist</option>
                    <option value="whitelist">✅ Whitelist</option>
                    <option value="suspended">⏸️ Sospeso</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Motivo</label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    required
                    placeholder="Motivo della blacklist/whitelist..."
                  />
                </FormGroup>

                <FormGroup>
                  <label>Severità</label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value as any }))}
                    required
                  >
                    <option value="low">🟢 Bassa</option>
                    <option value="medium">🟡 Media</option>
                    <option value="high">🟠 Alta</option>
                    <option value="critical">🔴 Critica</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Note Dettagliate</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Aggiungi dettagli sul motivo, comportamenti specifici, ecc..."
                    required
                  />
                  <div className="helper-text">
                    Fornisci dettagli specifici per giustificare la decisione
                  </div>
                </FormGroup>

                <FormGroup>
                  <label style={{ display: 'flex', alignItems: 'center', gap: designSystem.spacing.sm }}>
                    <input
                      type="checkbox"
                      checked={formData.is_permanent}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_permanent: e.target.checked }))}
                    />
                    Permanente
                  </label>
                </FormGroup>

                {!formData.is_permanent && (
                  <FormGroup>
                    <label>Data di Scadenza</label>
                    <input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <div className="helper-text">
                      Lascia vuoto per durata indefinita
                    </div>
                  </FormGroup>
                )}

                <ActionButtons style={{ marginTop: designSystem.spacing.xl }}>
                  <ActionButton type="button" variant="secondary" onClick={() => setShowModal(false)}>
                    Annulla
                  </ActionButton>
                  <ActionButton type="submit">
                    <SaveIcon size={16} />
                    {editingItem ? 'Aggiorna' : 'Aggiungi'}
                  </ActionButton>
                </ActionButtons>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default BlacklistPanel; 